import Axios from "../lib/Axios";

/**
 * User Information API Helper
 * 
 * Reusable API functions for user information across all pages
 */

/**
 * Get user information by userId
 * @param {string} userId - The user ID
 * @param {Object} filters - Filter parameters
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @returns {Promise} API response - Returns user object
 */
export const getUserInfo = async (userId, filters = {}) => {
  if (!userId) {
    throw new Error("userId is required");
  }

  const {
    limit = 50,
    page = 1,
  } = filters;

  const params = {
    userId: userId,
    limit: limit.toString(),
    page: page.toString(),
  };

  // GET /admin/users?userId={userId}
  const result = await Axios.get("admin/users", { params });
  const users = result?.data?.result?.users || [];
  const pagination = result?.data?.result?.pagination || {};

  return {
    user: users[0] || null, // Return first user (should be only one)
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Suspend a user
 * @param {Object} data - Suspend data
 * @param {string} data.user_id - The user ID to suspend
 * @param {string} data.reason - Reason for suspension
 * @returns {Promise} API response
 */
export const suspendUser = async (data) => {
  const { user_id, reason } = data;
  
  if (!user_id) {
    throw new Error("user_id is required");
  }
  if (!reason) {
    throw new Error("reason is required");
  }

  // POST /admin/users/suspend
  const result = await Axios.post("admin/users/suspend", {
    user_id,
    reason,
  });
  console.log(result)
  return result;
};

/**
 * Reactivate (unsuspend) a user
 * @param {Object} data - Reactivate data
 * @param {string} data.user_id - The user ID to reactivate
 * @returns {Promise} API response
 */
export const reactivateUser = async (data) => {
  const { user_id } = data;
  
  if (!user_id) {
    throw new Error("user_id is required");
  }

  // POST /admin/users/reactivate
  const result = await Axios.post("admin/users/reactivate", {
    user_id,
  });
  
  return result;
};

/**
 * Delete a user
 * @param {Object} data - Delete data
 * @param {string} data.userId - The user ID to delete
 * @returns {Promise} API response
 */
export const deleteUser = async (data) => {
  const { userId } = data;
  
  if (!userId) {
    throw new Error("userId is required");
  }

  // POST /admin/users/delete
  const result = await Axios.post("admin/users/delete", {
    userId,
  });
  
  return result;
};

/**
 * Add tokens to user(s)
 * @param {Object} data - Add tokens data
 * @param {number} data.tokens - Number of tokens to add
 * @param {Array<string>} data.userIds - Array of user IDs to add tokens to
 * @returns {Promise} API response
 */
export const addTokens = async (data) => {
  const { tokens, userIds } = data;
  
  if (!tokens || tokens <= 0) {
    throw new Error("tokens must be a positive number");
  }
  if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
    throw new Error("userIds must be a non-empty array");
  }

  // POST /admin/broadcast/add-tokens-by-user
  const result = await Axios.post("admin/broadcast/add-tokens-by-user", {
    tokens,
    userIds,
  });
  
  return result;
};

